import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { MapPin, DollarSign, ArrowRight, CheckCircle, Navigation, Clock, Weight, X, Route, Wifi, WifiOff, MessageCircle, Send, Phone, StickyNote, Camera, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import MapView from '@/components/MapView';
import { useFreightSocket } from '@/hooks/useFreightSocket';
import { useDriverGPS } from '@/hooks/useDriverGPS';
import PODCapture from '@/components/driver/PODCapture';
import { getLoadStatusClasses } from '@/lib/statusColors';

const milesToKm = (miles) => Math.round((miles || 0) * 1.60934);
const ratePerKm = (rate, miles) => ((rate || 0) / ((miles || 1) * 1.60934)).toFixed(2);

const STATUS_LABELS = {
  assigned: 'ASSIGNED', at_pickup: 'AT PICKUP', in_transit: 'IN TRANSIT', at_delivery: 'AT DELIVERY',
};
const NEXT_STATUS = {
  assigned: 'at_pickup', at_pickup: 'in_transit', in_transit: 'at_delivery', at_delivery: 'completed',
};
const NEXT_LABELS = {
  assigned: 'ARRIVED AT PICKUP', at_pickup: 'START TRANSIT', in_transit: 'ARRIVED AT DELIVERY', at_delivery: 'MARK COMPLETED',
};

function ContactPanel({ load, token, user, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    api.get(`/messages/${load.load_id}`)
      .then(r => setMessages(r.data))
      .catch(() => {});
  }, [token, load.load_id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim()) return;
    setSending(true);
    try {
      const res = await api.post('/messages', { load_id: load.load_id, content: input.trim() });
      setMessages(prev => [...prev, res.data]);
      setInput('');
    } catch { toast.error('Failed to send'); }
    setSending(false);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-background flex flex-col" data-testid="contact-panel">
      <div className="bg-card text-foreground px-4 py-3 flex items-center gap-3 shrink-0">
        <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg" data-testid="contact-close">
          <X className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <p className="text-sm font-semibold">{load.cargo_description}</p>
          <p className="text-xs text-muted-foreground">{load.pickup_name || load.pickup_address?.split(',')[0]} &rarr; {load.delivery_name || load.delivery_address?.split(',')[0]}</p>
        </div>
        <Phone className="w-5 h-5 text-muted-foreground" />
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-surface">
        {messages.length === 0 && (
          <div className="text-center text-muted-foreground text-sm py-12" data-testid="no-messages">
            No messages yet. Start the conversation.
          </div>
        )}
        {messages.map(m => (
          <div key={m.message_id} className={`flex ${m.sender_id === user.user_id ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${m.sender_id === user.user_id ? 'bg-brand-accent text-brand-accent-foreground' : 'bg-background border border-border text-foreground'}`}
              data-testid={`message-${m.message_id}`}>
              {m.sender_id !== user.user_id && (
                <p className="text-[10px] font-bold text-muted-foreground mb-0.5">{m.sender_name} ({m.sender_role})</p>
              )}
              <p className="text-sm">{m.content}</p>
              <p className={`text-[10px] mt-1 ${m.sender_id === user.user_id ? 'text-brand-accent-foreground/60' : 'text-muted-foreground'}`}>
                {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
      <div className="border-t border-border px-4 py-3 flex gap-2 shrink-0 bg-background">
        <Input value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSend()}
          placeholder="Type a message..."
          className="rounded-xl h-11 flex-1" data-testid="message-input" />
        <Button onClick={handleSend} disabled={sending || !input.trim()}
          className="bg-brand-accent hover:bg-brand-accent/90 text-brand-accent-foreground rounded-xl h-11 px-4" data-testid="message-send">
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

export default function DriverDashboard() {
  const { token, user } = useAuth();
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCard, setExpandedCard] = useState(null);
  const [gpsLoad, setGpsLoad] = useState(null);
  const [contactLoad, setContactLoad] = useState(null);
  const [noteLoad, setNoteLoad] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [notePhotos, setNotePhotos] = useState([]);
  const [noteSaving, setNoteSaving] = useState(false);
  const { lastEvent, connected } = useFreightSocket(token, user);

  const fetchLoads = () => {
    api.get('/loads')
      .then(r => setLoads(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchLoads(); }, [token]);

  useEffect(() => {
    if (!lastEvent) return;
    if (lastEvent.type === 'load_update') {
      setLoads(prev => {
        const exists = prev.some(l => l.load_id === lastEvent.load.load_id);
        if (exists) return prev.map(l => l.load_id === lastEvent.load.load_id ? lastEvent.load : l);
        return [...prev, lastEvent.load];
      });
      if (gpsLoad?.load_id === lastEvent.load.load_id) {
        setGpsLoad(lastEvent.load);
        if (['at_pickup', 'at_delivery'].includes(lastEvent.load.status) && lastEvent.load.status !== gpsLoad.status) {
          toast.info(`Geofence: Status auto-updated to ${lastEvent.load.status.replace('_', ' ')}`);
        }
      }
    }
  }, [lastEvent]);

  // Active = assigned (heading to pickup)
  const activeLoads = loads.filter(l => l.status === 'assigned' && l.driver_id === user?.user_id);
  // Picked Up = at_pickup, in_transit, at_delivery (have cargo)
  const pickedUpLoads = loads.filter(l => ['at_pickup', 'in_transit', 'at_delivery'].includes(l.status) && l.driver_id === user?.user_id);
  const availableLoads = loads.filter(l => l.status === 'posted');
  const allDriverLoads = [...activeLoads, ...pickedUpLoads];
  const primaryActiveLoad = allDriverLoads[0];

  useDriverGPS(token, user, primaryActiveLoad?.load_id);

  const [podLoadId, setPodLoadId] = useState(null);

  const handleStatusUpdate = async (loadId, newStatus) => {
    // Intercept completed status to show POD capture
    if (newStatus === 'completed') {
      setPodLoadId(loadId);
      return;
    }
    try {
      await api.put(`/loads/${loadId}/status`, { status: newStatus });
      toast.success(`Status updated to ${newStatus.replace('_', ' ')}`);
      fetchLoads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const handleAcceptLoad = async (loadId) => {
    try {
      await api.put(`/loads/${loadId}/assign`, { driver_id: user.user_id });
      toast.success('Load accepted!');
      fetchLoads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept');
    }
  };

  const toggleExpand = (loadId) => setExpandedCard(expandedCard === loadId ? null : loadId);

  if (loading) return <div className="space-y-4">{Array(3).fill(0).map((_, i) => <div key={i} className="h-32 bg-muted rounded-2xl animate-pulse" />)}</div>;

  // Contact panel
  if (contactLoad) {
    return <ContactPanel load={contactLoad} token={token} user={user} onClose={() => setContactLoad(null)} />;
  }

  const allMarkers = allDriverLoads.flatMap(l => [
    { id: `${l.load_id}-p`, lat: l.pickup_lat, lng: l.pickup_lng, label: l.pickup_name || l.pickup_address, type: 'pickup' },
    { id: `${l.load_id}-d`, lat: l.delivery_lat, lng: l.delivery_lng, label: l.delivery_name || l.delivery_address, type: 'delivery' },
  ]);

  // GPS Navigation View
  if (gpsLoad) {
    const gpsMarkers = [
      { id: 'p', lat: gpsLoad.pickup_lat, lng: gpsLoad.pickup_lng, label: gpsLoad.pickup_name || gpsLoad.pickup_address, type: 'pickup' },
      { id: 'd', lat: gpsLoad.delivery_lat, lng: gpsLoad.delivery_lng, label: gpsLoad.delivery_name || gpsLoad.delivery_address, type: 'delivery' },
    ];
    return (
      <div className="fixed inset-0 z-[100] bg-background flex flex-col" data-testid="gps-nav-view">
        <div className="bg-card text-foreground px-4 py-3 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <button onClick={() => setGpsLoad(null)} className="p-2 hover:bg-muted rounded-lg" data-testid="gps-close">
              <X className="w-5 h-5" />
            </button>
            <div>
              <p className="font-semibold text-sm">{gpsLoad.cargo_description}</p>
              <Badge className={`text-[10px] mt-0.5 ${getLoadStatusClasses(gpsLoad.status)}`}>{STATUS_LABELS[gpsLoad.status]}</Badge>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => { setGpsLoad(null); setContactLoad(gpsLoad); }}
              className="p-2 hover:bg-muted rounded-lg" data-testid="gps-contact">
              <MessageCircle className="w-5 h-5 text-brand-accent" />
            </button>
            <div className="text-right">
              <p className="text-lg font-bold text-brand-accent">${gpsLoad.rate?.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">{milesToKm(gpsLoad.distance_miles)} km</p>
            </div>
          </div>
        </div>
        <div className="flex-1 relative">
          <MapView markers={gpsMarkers} loads={[gpsLoad]} dark={false} zoom={7} />
        </div>
        <div className="bg-background border-t border-border px-4 py-4 space-y-3 shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 text-sm">
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                <span className="text-foreground font-medium">{gpsLoad.pickup_name || 'Pickup'}</span>
                <span className="text-muted-foreground truncate text-xs">{gpsLoad.pickup_address}</span>
              </div>
              <div className="flex items-center gap-2 text-sm mt-1">
                <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                <span className="text-foreground font-medium">{gpsLoad.delivery_name || 'Delivery'}</span>
                <span className="text-muted-foreground truncate text-xs">{gpsLoad.delivery_address}</span>
              </div>
            </div>
          </div>
          {NEXT_STATUS[gpsLoad.status] && (
            <Button onClick={() => handleStatusUpdate(gpsLoad.load_id, NEXT_STATUS[gpsLoad.status])}
              className="w-full h-14 text-base font-bold rounded-2xl bg-brand-accent hover:bg-brand-accent/90 text-brand-accent-foreground"
              data-testid="gps-status-btn">
              <Navigation className="w-5 h-5 mr-2" /> {NEXT_LABELS[gpsLoad.status]}
            </Button>
          )}
        </div>
      </div>
    );
  }

  const renderLoadCard = (load, variant = 'active') => {
    const isExpanded = expandedCard === load.load_id;
    return (
      <Card key={load.load_id}
        className="rounded-2xl border-border shadow-sm overflow-hidden transition-all duration-300 cursor-pointer hover:shadow-md"
        data-testid={`${variant}-load-${load.load_id}`}
        onClick={() => toggleExpand(load.load_id)}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <Badge className={`text-xs font-bold rounded-lg border ${getLoadStatusClasses(load.status)}`}>
              {STATUS_LABELS[load.status] || load.status?.replace('_', ' ').toUpperCase()}
            </Badge>
            <span className="text-lg font-bold text-foreground">${load.rate?.toLocaleString()}</span>
          </div>
          <h3 className="font-semibold text-base mb-2">{load.cargo_description}</h3>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
            <div className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
            <span className="font-medium text-foreground">{load.pickup_name || 'Pickup'}</span>
            <span className="truncate text-xs">{load.pickup_address}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
            <div className="w-2 h-2 rounded-full bg-red-500 shrink-0" />
            <span className="font-medium text-foreground">{load.delivery_name || 'Delivery'}</span>
            <span className="truncate text-xs">{load.delivery_address}</span>
          </div>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span>{milesToKm(load.distance_miles)} km</span>
            <span>{load.weight_lbs?.toLocaleString()} lbs</span>
            <span className="text-foreground font-bold">${ratePerKm(load.rate, load.distance_miles)}/km</span>
          </div>

          {isExpanded && (
            <div className="border-t border-zinc-100 pt-3 mt-3 space-y-3 animate-slide-up" data-testid={`details-${load.load_id}`}
              onClick={e => e.stopPropagation()}>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-surface rounded-xl p-2">
                  <DollarSign className="w-4 h-4 mx-auto text-foreground mb-1" />
                  <p className="text-xs text-muted-foreground">Earnings</p>
                  <p className="font-bold text-sm">${load.rate?.toLocaleString()}</p>
                </div>
                <div className="bg-surface rounded-xl p-2">
                  <Route className="w-4 h-4 mx-auto text-cyan-500 mb-1" />
                  <p className="text-xs text-muted-foreground">Distance</p>
                  <p className="font-bold text-sm">{milesToKm(load.distance_miles)} km</p>
                </div>
                <div className="bg-surface rounded-xl p-2">
                  <Weight className="w-4 h-4 mx-auto text-amber-500 mb-1" />
                  <p className="text-xs text-muted-foreground">Weight</p>
                  <p className="font-bold text-sm">{(load.weight_lbs/1000).toFixed(0)}k lbs</p>
                </div>
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Rate: ${ratePerKm(load.rate, load.distance_miles)}/km</span>
                <span>Created: {new Date(load.created_at).toLocaleDateString()}</span>
              </div>
              {load.notes && <p className="text-xs text-muted-foreground italic bg-surface rounded-lg p-2">{load.notes}</p>}
              <div className="flex gap-2">
                <Button onClick={() => setContactLoad(load)}
                  className="flex-1 h-12 rounded-xl bg-muted hover:bg-zinc-200 dark:hover:bg-muted/80 text-foreground font-bold"
                  data-testid={`contact-${load.load_id}`}>
                  <MessageCircle className="w-4 h-4 mr-2" /> Contact
                </Button>
                <Button onClick={() => { setNoteLoad(load); setNoteText(''); setNotePhotos([]); }}
                  className="flex-1 h-12 rounded-xl bg-muted hover:bg-zinc-200 dark:hover:bg-muted/80 text-foreground font-bold"
                  data-testid={`note-${load.load_id}`}>
                  <StickyNote className="w-4 h-4 mr-2" /> Note
                </Button>
                <Button onClick={() => setGpsLoad(load)}
                  className="flex-1 h-12 rounded-xl bg-card hover:bg-muted text-foreground font-bold"
                  data-testid={`navigate-${load.load_id}`}>
                  <Navigation className="w-4 h-4 mr-2" /> Navigate
                </Button>
              </div>
              {NEXT_STATUS[load.status] && (
                <Button onClick={() => handleStatusUpdate(load.load_id, NEXT_STATUS[load.status])}
                  className="w-full h-12 rounded-xl bg-brand-accent hover:bg-brand-accent/90 text-brand-accent-foreground font-bold"
                  data-testid={`update-status-${load.load_id}`}>
                  <Navigation className="w-5 h-5 mr-2" /> {NEXT_LABELS[load.status]}
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-4" data-testid="driver-dashboard">
      {/* Connection Status */}
      <div className="flex items-center justify-end gap-2" data-testid="ws-status">
        {connected ? (
          <span className="inline-flex items-center gap-1.5 text-xs text-emerald-600 bg-emerald-50 border border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20 rounded-full px-3 py-1">
            <Wifi className="w-3 h-3" /> Live
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground bg-muted border border-border rounded-full px-3 py-1">
            <WifiOff className="w-3 h-3" /> Reconnecting...
          </span>
        )}
        {primaryActiveLoad && (
          <span className="inline-flex items-center gap-1.5 text-xs text-blue-600 bg-blue-50 border border-blue-200 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/20 rounded-full px-3 py-1" data-testid="gps-reporting">
            <Navigation className="w-3 h-3" /> GPS Active
          </span>
        )}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="bg-brand-accent/10 border-brand-accent/20 dark:bg-card dark:border-border rounded-2xl">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-foreground">{allDriverLoads.length}</p>
            <p className="text-[10px] text-foreground dark:text-muted-foreground font-bold tracking-wider">IN PROGRESS</p>
          </CardContent>
        </Card>
        <Card className="bg-yellow-50 border-yellow-200 dark:bg-card dark:border-border rounded-2xl">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{availableLoads.length}</p>
            <p className="text-[10px] text-yellow-500 dark:text-muted-foreground font-bold tracking-wider">AVAILABLE</p>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200 dark:bg-card dark:border-border rounded-2xl">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{milesToKm(allDriverLoads.reduce((s, l) => s + (l.distance_miles || 0), 0))} km</p>
            <p className="text-[10px] text-blue-500 dark:text-muted-foreground font-bold tracking-wider">ON ROAD</p>
          </CardContent>
        </Card>
      </div>

      {/* Active Load Map */}
      {allMarkers.length > 0 && (
        <Card className="rounded-2xl border-border overflow-hidden" data-testid="driver-map-card">
          <div className="h-[250px] map-container-light">
            <MapView markers={allMarkers} loads={allDriverLoads} dark={false} zoom={6} />
          </div>
        </Card>
      )}

      <Tabs defaultValue="active" className="w-full">
        <TabsList className="w-full bg-muted rounded-xl h-12">
          <TabsTrigger value="active" className="flex-1 rounded-lg h-10 text-sm font-bold" data-testid="tab-active">
            ACTIVE ({activeLoads.length})
          </TabsTrigger>
          <TabsTrigger value="picked_up" className="flex-1 rounded-lg h-10 text-sm font-bold" data-testid="tab-picked-up">
            PICKED UP ({pickedUpLoads.length})
          </TabsTrigger>
          <TabsTrigger value="available" className="flex-1 rounded-lg h-10 text-sm font-bold" data-testid="tab-available">
            AVAILABLE ({availableLoads.length})
          </TabsTrigger>
        </TabsList>

        {/* Active Loads (assigned - heading to pickup) */}
        <TabsContent value="active" className="space-y-3 mt-3">
          {activeLoads.length === 0 ? (
            <Card className="rounded-2xl border-border"><CardContent className="p-8 text-center text-muted-foreground">No active loads — accept a job or check Picked Up</CardContent></Card>
          ) : activeLoads.map(load => renderLoadCard(load, 'active'))}
        </TabsContent>

        {/* Picked Up Loads (at_pickup, in_transit, at_delivery) */}
        <TabsContent value="picked_up" className="space-y-3 mt-3">
          {pickedUpLoads.length === 0 ? (
            <Card className="rounded-2xl border-border"><CardContent className="p-8 text-center text-muted-foreground">No picked up loads</CardContent></Card>
          ) : pickedUpLoads.map(load => renderLoadCard(load, 'picked-up'))}
        </TabsContent>

        {/* Available Loads */}
        <TabsContent value="available" className="space-y-3 mt-3">
          {availableLoads.length === 0 ? (
            <Card className="rounded-2xl border-border"><CardContent className="p-8 text-center text-muted-foreground">No loads available right now</CardContent></Card>
          ) : availableLoads.map(load => (
            <Card key={load.load_id}
              className="rounded-2xl border-border shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition-all"
              data-testid={`available-load-${load.load_id}`}
              onClick={() => toggleExpand(load.load_id)}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-sm">{load.cargo_description}</h3>
                  <span className="text-lg font-bold text-foreground">${load.rate?.toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                  <span className="font-medium text-foreground">{load.pickup_name || 'Pickup'}</span>
                  <span className="truncate text-xs">{load.pickup_address}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                  <div className="w-2 h-2 rounded-full bg-red-500 shrink-0" />
                  <span className="font-medium text-foreground">{load.delivery_name || 'Delivery'}</span>
                  <span className="truncate text-xs">{load.delivery_address}</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2">
                  <span>{load.weight_lbs?.toLocaleString()} lbs</span>
                  <span>{milesToKm(load.distance_miles)} km</span>
                  <span className="text-foreground font-bold">${ratePerKm(load.rate, load.distance_miles)}/km</span>
                </div>

                {expandedCard === load.load_id && (
                  <div className="border-t border-zinc-100 pt-3 space-y-3 animate-slide-up"
                    data-testid={`details-available-${load.load_id}`}
                    onClick={e => e.stopPropagation()}>
                    <div className="h-[180px] rounded-xl overflow-hidden border border-border">
                      <MapView
                        markers={[
                          { id: 'p', lat: load.pickup_lat, lng: load.pickup_lng, label: load.pickup_name || load.pickup_address, type: 'pickup' },
                          { id: 'd', lat: load.delivery_lat, lng: load.delivery_lng, label: load.delivery_name || load.delivery_address, type: 'delivery' },
                        ]}
                        loads={[load]} dark={false} zoom={6}
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="bg-surface rounded-xl p-2">
                        <DollarSign className="w-4 h-4 mx-auto text-foreground mb-1" />
                        <p className="text-xs text-muted-foreground">Total Pay</p>
                        <p className="font-bold text-sm">${load.rate?.toLocaleString()}</p>
                      </div>
                      <div className="bg-surface rounded-xl p-2">
                        <Route className="w-4 h-4 mx-auto text-cyan-500 mb-1" />
                        <p className="text-xs text-muted-foreground">Distance</p>
                        <p className="font-bold text-sm">{milesToKm(load.distance_miles)} km</p>
                      </div>
                      <div className="bg-surface rounded-xl p-2">
                        <Clock className="w-4 h-4 mx-auto text-amber-500 mb-1" />
                        <p className="text-xs text-muted-foreground">Est. Time</p>
                        <p className="font-bold text-sm">{Math.round(milesToKm(load.distance_miles) / 80)}h</p>
                      </div>
                    </div>
                    {load.notes && <p className="text-xs text-muted-foreground italic bg-surface rounded-lg p-2">{load.notes}</p>}
                  </div>
                )}

                <Button onClick={(e) => { e.stopPropagation(); handleAcceptLoad(load.load_id); }}
                  className="w-full h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold mt-2"
                  data-testid={`accept-load-${load.load_id}`}>
                  <CheckCircle className="w-5 h-5 mr-2" /> ACCEPT LOAD
                </Button>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      {/* POD Capture Modal */}
      {podLoadId && (
        <PODCapture
          loadId={podLoadId}
          onComplete={() => { setPodLoadId(null); setGpsLoad(null); fetchLoads(); }}
          onCancel={() => setPodLoadId(null)}
        />
      )}

      {/* Add Note Dialog */}
      <Dialog open={!!noteLoad} onOpenChange={() => setNoteLoad(null)}>
        <DialogContent className="bg-background max-w-md">
          <DialogHeader><DialogTitle>Add Note</DialogTitle></DialogHeader>
          {noteLoad && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Add a note to load: {noteLoad.cargo_description}</p>
              <Textarea
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                placeholder="Type your note here..."
                className="min-h-[100px] rounded-xl"
              />
              <div>
                <label className="text-xs text-muted-foreground font-medium mb-2 block">Photos (optional)</label>
                <div className="flex flex-wrap gap-2">
                  {notePhotos.map((p, i) => (
                    <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border border-border">
                      <img src={URL.createObjectURL(p)} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => setNotePhotos(prev => prev.filter((_, j) => j !== i))}
                        className="absolute top-0 right-0 bg-red-500 text-white rounded-bl p-0.5">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  <label className="w-16 h-16 rounded-lg border-2 border-dashed border-border flex items-center justify-center cursor-pointer hover:border-muted-foreground transition-colors">
                    <Camera className="w-5 h-5 text-muted-foreground" />
                    <input type="file" accept="image/*" capture="environment" multiple className="hidden"
                      onChange={e => { if (e.target.files) setNotePhotos(prev => [...prev, ...Array.from(e.target.files)]); }} />
                  </label>
                </div>
              </div>
              <Button
                onClick={async () => {
                  if (!noteText.trim() && notePhotos.length === 0) return;
                  setNoteSaving(true);
                  try {
                    const photoUrls = [];
                    for (const photo of notePhotos) {
                      const path = `notes/${noteLoad.load_id}/${Date.now()}_${photo.name}`;
                      const { error } = await supabase.storage.from('uploads').upload(path, photo);
                      if (!error) {
                        const { data: urlData } = supabase.storage.from('uploads').getPublicUrl(path);
                        photoUrls.push(urlData.publicUrl);
                      }
                    }
                    await api.post(`/loads/${noteLoad.load_id}/notes`, {
                      content: noteText.trim(),
                      photo_urls: photoUrls,
                    });
                    toast.success('Note added');
                    setNoteLoad(null);
                  } catch {
                    toast.error('Failed to add note');
                  }
                  setNoteSaving(false);
                }}
                disabled={noteSaving || (!noteText.trim() && notePhotos.length === 0)}
                className="w-full h-12 rounded-xl bg-brand-accent hover:bg-brand-accent/90 text-brand-accent-foreground font-bold">
                {noteSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <StickyNote className="w-4 h-4 mr-2" />}
                {noteSaving ? 'Saving...' : 'Submit Note'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
